// features/inventory/pages/StockLevelsPage.tsx
/**
 * Stock Levels Page — View, search, and filter all stock across branches.
 * ✅ FIXED: Pagination uses accurate total count (global when no filters, meta when filtered)
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { Card, CardContent } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Pagination } from '@/components/ui/Pagination';
import {
    getStockLevels,
    getLowStockAlerts,
    exportStockLevels,
    confirmStock,
    getStockConfirmationStatus,
    StockLevel,
} from '@/features/stock/services/stockApi';
import { getBranches } from '@/features/settings/services/settingsApi';
import {
    Search,
    RefreshCw,
    Package,
    AlertTriangle,
    CheckCircle,
    XCircle,
    Layers,
    X,
    Store,
    Download,
    Upload,
    ShieldCheck,
    Eye,
    LayoutGrid,
    List,
    ArrowUpDown,
    History,
    RotateCcw,
    Bell,
    ArrowRight,
    Filter,
    TrendingDown,
    TrendingUp,
    Wifi,
    WifiOff,
    Barcode,
    ArrowRightLeft,
    ClipboardList,
    Clock,
} from 'lucide-react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';

// ============================================================
// CONSTANTS
// ============================================================

const PAGE_SIZE = 30;
const PAGE_SIZE_OPTIONS = [12, 24, 30, 48, 100];

type StockFilter = 'all' | 'low' | 'out' | 'in_stock';
type ConfirmationFilter = 'all' | 'pending' | 'counted' | 'confirmed';
type SortField = 'productName' | 'quantityOnHand' | 'availableQuantity' | 'updatedAt';
type SortOrder = 'asc' | 'desc';
type ViewMode = 'table' | 'card';

// ============================================================
// SUB-COMPONENTS
// ============================================================

function LowStockAlertBanner({
    alerts,
}: {
    alerts: any[];
}) {
    const navigate = useNavigate();

    if (alerts.length === 0) return null;

    return (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
            <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 overflow-hidden">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                            <motion.div
                                animate={{ rotate: [0, 10, -10, 0] }}
                                transition={{ duration: 2, repeat: Infinity }}
                                className="p-2 rounded-xl bg-amber-100"
                            >
                                <Bell className="w-5 h-5 text-amber-600" />
                            </motion.div>
                            <div>
                                <h3 className="font-bold text-amber-800">
                                    {alerts.length} Product{alerts.length !== 1 ? 's' : ''} Need
                                    {alerts.length === 1 ? 's' : ''} Attention
                                </h3>
                                <p className="text-xs text-amber-600">
                                    Stock below reorder threshold
                                </p>
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                        {alerts.slice(0, 6).map((alert: any) => (
                            <div
                                key={`${alert.productId}-${alert.branchId}`}
                                onClick={() =>
                                    navigate(`/inventory/levels/${alert.productId}`)
                                }
                                className="flex items-center justify-between p-3 bg-white rounded-xl border border-amber-200 cursor-pointer hover:shadow-md hover:border-amber-300 transition-all group"
                            >
                                <div className="min-w-0 flex-1">
                                    <p className="text-sm font-semibold text-slate-800 truncate group-hover:text-emerald-600 transition-colors">
                                        {alert.productName || 'Unknown'}
                                    </p>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <span className="text-xs text-slate-400">
                                            {alert.branchName || 'Warehouse'}
                                        </span>
                                        <span className="text-xs font-bold text-amber-600">
                                            {parseFloat(
                                                alert.availableQuantity || '0'
                                            ).toLocaleString()}{' '}
                                            left
                                        </span>
                                    </div>
                                </div>
                                <ArrowRight className="w-4 h-4 text-amber-400 flex-shrink-0 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </motion.div>
    );
}

function StockCardView({
    items,
    navigate,
}: {
    items: StockLevel[];
    navigate: ReturnType<typeof useNavigate>;
}) {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {items.map((item, idx) => {
                const available = parseFloat(item.availableQuantity || '0');
                const threshold = (item as any).lowStockThreshold || 10;
                const isOut = available <= 0;
                const isLow = !isOut && available <= threshold;
                const isConfirmed = item.confirmationStatus === 'confirmed';

                return (
                    <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.02 }}
                        whileHover={{ y: -4 }}
                        onClick={() =>
                            navigate(`/inventory/levels/${item.productId}`)
                        }
                        className={clsx(
                            'bg-white rounded-2xl border-2 p-5 cursor-pointer transition-all hover:shadow-xl relative overflow-hidden',
                            isOut
                                ? 'border-rose-200 bg-rose-50/20'
                                : isLow
                                    ? 'border-amber-200 bg-amber-50/20'
                                    : 'border-slate-200 hover:border-emerald-300'
                        )}
                    >
                        <div
                            className={clsx(
                                'absolute top-0 left-0 right-0 h-1',
                                isOut
                                    ? 'bg-rose-500'
                                    : isLow
                                        ? 'bg-amber-500'
                                        : isConfirmed
                                            ? 'bg-emerald-500'
                                            : 'bg-slate-300'
                            )}
                        />
                        <div className="flex items-start justify-between mb-3 mt-1">
                            <div
                                className={clsx(
                                    'p-2.5 rounded-xl',
                                    isOut
                                        ? 'bg-rose-100'
                                        : isLow
                                            ? 'bg-amber-100'
                                            : 'bg-emerald-100'
                                )}
                            >
                                <Package
                                    className={clsx(
                                        'w-5 h-5',
                                        isOut
                                            ? 'text-rose-500'
                                            : isLow
                                                ? 'text-amber-500'
                                                : 'text-emerald-500'
                                    )}
                                />
                            </div>
                            <div className="flex flex-col items-end gap-1">
                                {isOut ? (
                                    <Badge variant="danger" size="sm">
                                        Out of Stock
                                    </Badge>
                                ) : isLow ? (
                                    <Badge variant="warning" size="sm">
                                        Low Stock
                                    </Badge>
                                ) : (
                                    <Badge variant="success" size="sm">
                                        In Stock
                                    </Badge>
                                )}
                                {!isConfirmed && (
                                    <Badge variant="warning" size="sm">
                                        Pending
                                    </Badge>
                                )}
                            </div>
                        </div>
                        <h3 className="font-bold text-sm text-slate-900 truncate mb-1">
                            {item.productName || 'Unknown Product'}
                        </h3>
                        <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-2 truncate">
                            {item.variantName && <span>{item.variantName}</span>}
                            {item.sku && (
                                <span className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500">
                                    {item.sku}
                                </span>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-2 mt-3 pt-3 border-t border-slate-100">
                            <div className="text-center">
                                <p className="text-[10px] text-slate-400 uppercase">
                                    On Hand
                                </p>
                                <p
                                    className={clsx(
                                        'text-lg font-bold',
                                        isOut ? 'text-rose-600' : 'text-slate-700'
                                    )}
                                >
                                    {parseFloat(
                                        item.quantityOnHand || '0'
                                    ).toLocaleString()}
                                </p>
                            </div>
                            <div className="text-center">
                                <p className="text-[10px] text-slate-400 uppercase">
                                    Available
                                </p>
                                <p
                                    className={clsx(
                                        'text-lg font-bold',
                                        isOut ? 'text-rose-600' : 'text-slate-700'
                                    )}
                                >
                                    {parseFloat(
                                        item.availableQuantity || '0'
                                    ).toLocaleString()}
                                </p>
                            </div>
                        </div>
                        {item.branchName && (
                            <p className="text-[10px] text-slate-400 mt-2 flex items-center gap-1">
                                <Store className="w-3 h-3" /> {item.branchName}
                            </p>
                        )}
                    </motion.div>
                );
            })}
        </div>
    );
}

function StockTableView({
    items,
    isSingleStore,
    navigate,
}: {
    items: StockLevel[];
    isSingleStore: boolean;
    navigate: ReturnType<typeof useNavigate>;
}) {
    const formatNumber = (val: string | undefined): string => {
        if (!val) return '0';
        const num = parseFloat(val);
        return isNaN(num) ? '0' : num.toLocaleString(undefined, {
            maximumFractionDigits: 0,
        });
    };

    return (
        <Card className="overflow-hidden shadow-sm border border-slate-200">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-100">
                    <thead>
                        <tr className="bg-slate-50/50">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">
                                Product
                            </th>
                            {!isSingleStore && (
                                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase hidden sm:table-cell">
                                    Branch
                                </th>
                            )}
                            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase">
                                On Hand
                            </th>
                            <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 uppercase hidden md:table-cell">
                                Available
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-24">
                                Status
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-24">
                                Confirmed
                            </th>
                            <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 uppercase w-28">
                                Actions
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {items.map((item, idx) => {
                            const onHand = parseFloat(item.quantityOnHand || '0');
                            const available = parseFloat(
                                item.availableQuantity || '0'
                            );
                            const threshold = (item as any).lowStockThreshold || 10;
                            const isOut = available <= 0;
                            const isLow = !isOut && available <= threshold;
                            const isConfirmed =
                                item.confirmationStatus === 'confirmed';

                            return (
                                <motion.tr
                                    key={item.id}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        delay: Math.min(idx * 0.01, 0.3),
                                    }}
                                    className={clsx(
                                        'hover:bg-slate-50 transition-colors group',
                                        isOut && 'bg-rose-50/20',
                                        isLow && 'bg-amber-50/20'
                                    )}
                                >
                                    <td className="px-4 py-3">
                                        <div
                                            className="flex items-center gap-3 cursor-pointer"
                                            onClick={() =>
                                                navigate(
                                                    `/inventory/levels/${item.productId}`
                                                )
                                            }
                                        >
                                            <div
                                                className={clsx(
                                                    'w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0',
                                                    isOut
                                                        ? 'bg-rose-100'
                                                        : isLow
                                                            ? 'bg-amber-100'
                                                            : 'bg-emerald-100'
                                                )}
                                            >
                                                <Package
                                                    className={clsx(
                                                        'w-4 h-4',
                                                        isOut
                                                            ? 'text-rose-500'
                                                            : isLow
                                                                ? 'text-amber-500'
                                                                : 'text-emerald-500'
                                                    )}
                                                />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-slate-900 truncate max-w-[200px] group-hover:text-emerald-600 transition-colors">
                                                    {item.productName || 'Unknown'}
                                                </p>
                                                <div className="flex items-center gap-1.5 text-xs text-slate-400">
                                                    {item.variantName && (
                                                        <span>{item.variantName}</span>
                                                    )}
                                                    {item.sku && (
                                                        <span className="font-mono text-[10px] bg-slate-100 px-1 py-0.5 rounded text-slate-500">
                                                            {item.sku}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </td>
                                    {!isSingleStore && (
                                        <td className="px-4 py-3 hidden sm:table-cell">
                                            <div className="flex items-center gap-1.5">
                                                <Store className="w-3 h-3 text-slate-400" />
                                                <span className="text-xs text-slate-600">
                                                    {item.branchName || 'Warehouse'}
                                                </span>
                                            </div>
                                        </td>
                                    )}
                                    <td className="px-4 py-3 text-right">
                                        <span
                                            className={clsx(
                                                'text-sm font-bold tabular-nums',
                                                isOut
                                                    ? 'text-rose-600'
                                                    : isLow
                                                        ? 'text-amber-600'
                                                        : 'text-slate-800'
                                            )}
                                        >
                                            {formatNumber(item.quantityOnHand)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right hidden md:table-cell">
                                        <span
                                            className={clsx(
                                                'text-sm tabular-nums',
                                                isOut
                                                    ? 'text-rose-500 font-semibold'
                                                    : 'text-slate-500'
                                            )}
                                        >
                                            {formatNumber(item.availableQuantity)}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {isOut ? (
                                            <Badge variant="danger" size="sm">
                                                Out
                                            </Badge>
                                        ) : isLow ? (
                                            <Badge variant="warning" size="sm">
                                                Low
                                            </Badge>
                                        ) : (
                                            <Badge variant="success" size="sm">
                                                In Stock
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        {isConfirmed ? (
                                            <Badge variant="success" size="sm">
                                                ✓
                                            </Badge>
                                        ) : item.confirmationStatus === 'counted' ? (
                                            <Badge variant="info" size="sm">
                                                Counted
                                            </Badge>
                                        ) : (
                                            <Badge variant="warning" size="sm">
                                                Pending
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 text-center">
                                        <div className="flex items-center justify-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(
                                                        `/inventory/levels/${item.productId}`
                                                    );
                                                }}
                                                className="p-1.5 rounded-lg text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-colors"
                                                type="button"
                                                title="View stock details"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(
                                                        `/inventory/adjust?productId=${item.productId}&branchId=${item.branchId}`
                                                    );
                                                }}
                                                className="p-1.5 rounded-lg text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-colors"
                                                type="button"
                                                title="Adjust stock"
                                            >
                                                <RotateCcw className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(
                                                        `/inventory/transfers?productId=${item.productId}&fromBranchId=${item.branchId}`
                                                    );
                                                }}
                                                className="p-1.5 rounded-lg text-slate-400 hover:text-purple-600 hover:bg-purple-50 transition-colors"
                                                type="button"
                                                title="Transfer stock"
                                            >
                                                <ArrowRightLeft className="w-4 h-4" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    navigate(`/inventory/levels/${item.productId}?tab=transactions`);
                                                }}
                                                className="p-1.5 rounded-lg text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                                                type="button"
                                                title="View transaction history"
                                            >
                                                <History className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </motion.tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function StockLevelsPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const user = useAuthStore((s) => s.user);
    const selectedBranchId = useAuthStore((s) => s.selectedBranchId);
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    // Online/offline detection
    const [isOnline, setIsOnline] = useState<boolean>(
        typeof navigator !== 'undefined' ? navigator.onLine : true
    );

    useEffect(() => {
        const handleOnline = () => setIsOnline(true);
        const handleOffline = () => setIsOnline(false);
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Filters
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const [filter, setFilter] = useState<StockFilter>(
        (searchParams.get('filter') as StockFilter) || 'all'
    );
    const [confirmationFilter, setConfirmationFilter] =
        useState<ConfirmationFilter>('all');
    const [branchId, setBranchId] = useState<string>(
        selectedBranchId || ''
    );
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [sortBy, setSortBy] = useState<SortField>('updatedAt');
    const [order, setOrder] = useState<SortOrder>('desc');
    const [viewMode, setViewMode] = useState<ViewMode>('table');
    const [confirmingStock, setConfirmingStock] = useState(false);

    // Branches
    const { data: branches } = useQuery({
        queryKey: ['branches-stock', user?.tenantId],
        queryFn: async () => {
            try {
                const data = await getBranches(user!.tenantId);
                return Array.isArray(data) ? data : [];
            } catch {
                return [];
            }
        },
        enabled: !!user?.tenantId,
    });

    const branchesList = (branches || []) as Array<{
        id: string;
        name: string;
        isMainBranch?: boolean;
    }>;
    const isSingleStore = branchesList.length <= 1;

    // Auto-select branch
    useEffect(() => {
        if (branchesList.length > 0 && !branchId) {
            const preferred =
                selectedBranchId &&
                    branchesList.find((b: any) => b.id === selectedBranchId)
                    ? selectedBranchId
                    : branchesList.find((b: any) => b.isMainBranch)?.id ||
                    branchesList[0]?.id ||
                    '';
            setBranchId(preferred);
        }
    }, [branchesList, selectedBranchId, branchId]);

    // Stock levels query
    const {
        data: stockResponse,
        isLoading,
        isFetching,
        isError,
        refetch,
    } = useQuery({
        queryKey: [
            'stock-levels',
            user?.tenantId,
            search,
            filter,
            confirmationFilter,
            branchId,
            page,
            pageSize,
            sortBy,
            order,
        ],
        queryFn: async () => {
            const response = await getStockLevels(user!.tenantId, {
                search: search || undefined,
                branchId: branchId || undefined,
                lowStock: filter === 'low' ? true : undefined,
                outOfStock: filter === 'out' ? true : undefined,
                confirmationStatus:
                    confirmationFilter !== 'all'
                        ? confirmationFilter
                        : undefined,
                limit: pageSize,
                page,
                sortBy,
                order,
            });
            return response;
        },
        enabled: !!user?.tenantId,
        placeholderData: (prev) => prev,
    });

    // Low stock alerts
    const { data: lowStockAlerts } = useQuery({
        queryKey: ['low-stock-alerts', user?.tenantId, branchId],
        queryFn: () =>
            getLowStockAlerts(user!.tenantId, branchId || undefined),
        enabled: !!user?.tenantId,
        refetchInterval: 60_000,
    });

    // ✅ Confirmation status — now always fetched, even without branchId (global)
    const { data: confirmationStatus, refetch: refetchStatus } = useQuery({
        queryKey: [
            'stock-confirmation-status',
            user?.tenantId,
            branchId || 'all', // key includes 'all' to differentiate global vs branch
        ],
        queryFn: () =>
            getStockConfirmationStatus(
                user!.tenantId,
                branchId || '' // ✅ pass empty string instead of undefined to fix type error
            ),
        enabled: !!user?.tenantId, // always enabled
    });

    // Derived data
    const items: StockLevel[] = stockResponse?.data || [];
    const totalCountFromMeta: number = stockResponse?.meta?.totalCount ?? items.length ?? 0;
    const alerts = Array.isArray(lowStockAlerts) ? lowStockAlerts : [];

    // ✅ Global stats from confirmation status (accurate across all branches)
    const totalProductCount = confirmationStatus?.total ?? totalCountFromMeta;
    const confirmedCount = confirmationStatus?.confirmed || 0;
    const pendingCount = confirmationStatus?.pending || 0;
    const countedCount = confirmationStatus?.counted || 0;
    const lowStockCount = confirmationStatus?.lowStock ?? alerts.length ?? 0;
    const outOfStockCount = confirmationStatus?.outOfStock ?? 0;
    const inStockCount = totalProductCount - outOfStockCount;

    // ✅ Determine pagination total count:
    // - When NO filters active: use global totalProductCount
    // - When filters active: use meta.totalCount (fallback to items.length)
    const hasActiveFilters =
        search || filter !== 'all' || confirmationFilter !== 'all' || !!branchId;
    const paginationTotalCount = hasActiveFilters
        ? totalCountFromMeta
        : totalProductCount;

    const totalPages = Math.max(1, Math.ceil(paginationTotalCount / pageSize));

    // Handlers
    const clearFilters = useCallback(() => {
        setSearch('');
        setFilter('all');
        setConfirmationFilter('all');
        setBranchId('');
        setPage(1);
    }, []);

    const handlePageChange = useCallback(
        (newPage: number) => {
            if (
                newPage < 1 ||
                newPage > totalPages ||
                newPage === page
            )
                return;
            setPage(newPage);
            window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        [totalPages, page]
    );

    const handleExport = async () => {
        try {
            const blob = await exportStockLevels(user!.tenantId, {
                branchId: branchId || undefined,
                lowStock: filter === 'low' ? true : undefined,
                outOfStock: filter === 'out' ? true : undefined,
            });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `stock-levels-${new Date().toISOString().split('T')[0]
                }.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
            addToast({
                type: 'success',
                title: 'Stock levels exported',
            });
        } catch {
            addToast({ type: 'error', title: 'Export failed' });
        }
    };

    const handleConfirmStock = async () => {
        if (!branchId) {
            addToast({
                type: 'warning',
                title: 'Select a branch first',
            });
            return;
        }
        setConfirmingStock(true);
        try {
            const result = await confirmStock(user!.tenantId, branchId);
            addToast({
                type: 'success',
                title: 'Stock Confirmed',
                message: `${result.confirmed} items marked as sellable.`,
            });
            refetch();
            refetchStatus();
            queryClient.invalidateQueries({
                queryKey: ['low-stock-dash'],
            });
            queryClient.invalidateQueries({
                queryKey: ['confirm-status-dash'],
            });
        } catch (err: any) {
            addToast({
                type: 'error',
                title: 'Confirmation failed',
                message: err?.message,
            });
        } finally {
            setConfirmingStock(false);
        }
    };

    const formatNumber = (val: string | undefined): string => {
        if (!val) return '0';
        const num = parseFloat(val);
        return isNaN(num)
            ? '0'
            : num.toLocaleString(undefined, {
                maximumFractionDigits: 0,
            });
    };

    const getBranchName = useCallback(
        (id: string) => {
            if (!id) return 'Warehouse';
            return (
                branchesList.find((b: any) => b.id === id)?.name ||
                'Warehouse'
            );
        },
        [branchesList]
    );

    // Sync URL params
    useEffect(() => {
        const params: Record<string, string> = {};
        if (search) params.search = search;
        if (filter !== 'all') params.filter = filter;
        setSearchParams(params, { replace: true });
    }, [search, filter, setSearchParams]);

    // ============================================================
    // RENDER
    // ============================================================

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
            {/* Header */}
            <div className="bg-white/90 backdrop-blur-md border-b border-slate-200 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1600px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                        {/* Title */}
                        <div className="flex items-center gap-4">
                            <div className="p-2.5 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl shadow-lg shadow-emerald-500/25">
                                <Layers className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-slate-900">
                                    Stock Levels
                                </h1>
                                <p className="text-sm text-slate-500">
                                    {totalProductCount.toLocaleString()} products
                                    {alerts.length > 0 && (
                                        <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 rounded-full text-xs font-medium">
                                            <Bell className="w-3 h-3" />{' '}
                                            {alerts.length} alerts
                                        </span>
                                    )}
                                </p>
                            </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 flex-wrap">
                            {/* Online/Offline */}
                            {!isOnline ? (
                                <div className="flex items-center gap-1.5 px-3 py-2 bg-amber-50 border border-amber-200 rounded-xl">
                                    <WifiOff className="w-4 h-4 text-amber-600" />
                                    <span className="text-xs font-medium text-amber-700">
                                        Offline
                                    </span>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1.5 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
                                    <Wifi className="w-4 h-4 text-emerald-600" />
                                    <span className="text-xs font-medium text-emerald-700">
                                        Online
                                    </span>
                                </div>
                            )}

                            {/* Export */}
                            <button
                                onClick={handleExport}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <Download className="w-4 h-4" /> Export
                            </button>

                            {/* Refresh */}
                            <button
                                onClick={() => refetch()}
                                disabled={isFetching}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <RefreshCw
                                    className={clsx(
                                        'w-4 h-4',
                                        isFetching && 'animate-spin'
                                    )}
                                />
                            </button>

                            {/* Bulk Adjust */}
                            <button
                                onClick={() =>
                                    navigate('/inventory/adjust/bulk')
                                }
                                className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl hover:bg-slate-50 transition-all text-sm font-medium text-slate-700 shadow-sm"
                                type="button"
                            >
                                <Upload className="w-4 h-4" /> Bulk
                            </button>

                            {/* Adjust Stock */}
                            <button
                                onClick={() =>
                                    navigate('/inventory/adjust')
                                }
                                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-emerald-600 to-teal-600 text-white rounded-xl hover:from-emerald-700 hover:to-teal-700 transition-all font-semibold shadow-lg shadow-emerald-500/25 text-sm"
                                type="button"
                            >
                                <RotateCcw className="w-4 h-4" /> Adjust
                                Stock
                            </button>

                            {/* Confirm All Pending */}
                            {pendingCount > 0 && (
                                <button
                                    onClick={handleConfirmStock}
                                    disabled={confirmingStock}
                                    className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-xl hover:from-amber-600 transition-all text-sm font-bold shadow-md disabled:opacity-50"
                                    type="button"
                                >
                                    {confirmingStock ? (
                                        <Spinner
                                            size="xs"
                                            variant="white"
                                        />
                                    ) : (
                                        <ShieldCheck className="w-4 h-4" />
                                    )}{' '}
                                    Confirm {pendingCount} Items
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-[1600px] mx-auto p-6 lg:p-8 space-y-5">
                {/* ========== DESCRIPTIVE GLOBAL STATS ========== */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                    {/* Total Products */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 cursor-pointer hover:shadow-md transition-all"
                        onClick={() => { clearFilters(); }}>
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-slate-500 to-slate-700"><Layers className="w-4 h-4 text-white" /></div>
                            <span className="text-[10px] text-slate-400 font-medium">All Products</span>
                        </div>
                        <p className="text-2xl font-bold text-slate-900">{totalProductCount.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Across {isSingleStore ? '1 branch' : `${branchesList.length} branches`}</p>
                    </motion.div>

                    {/* In Stock */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-4 cursor-pointer hover:shadow-md transition-all"
                        onClick={() => { setFilter('in_stock'); setPage(1); }}>
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600"><CheckCircle className="w-4 h-4 text-white" /></div>
                            <span className="text-[10px] text-emerald-600 font-semibold">{totalProductCount > 0 ? `${Math.round((inStockCount / totalProductCount) * 100)}%` : '0%'}</span>
                        </div>
                        <p className="text-2xl font-bold text-emerald-700">{inStockCount.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{outOfStockCount === 0 ? 'All products have stock available' : `${outOfStockCount} out of stock`}</p>
                    </motion.div>

                    {/* Low Stock */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-amber-200 shadow-sm p-4 cursor-pointer hover:shadow-md transition-all"
                        onClick={() => { setFilter('low'); setPage(1); }}>
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600"><AlertTriangle className="w-4 h-4 text-white" /></div>
                            {lowStockCount > 0 && <span className="text-[10px] text-amber-600 font-semibold">{totalProductCount > 0 ? `${Math.round((lowStockCount / totalProductCount) * 100)}%` : '0%'}</span>}
                        </div>
                        <p className={clsx('text-2xl font-bold', lowStockCount > 0 ? 'text-amber-600' : 'text-slate-900')}>{lowStockCount.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{lowStockCount > 0 ? `${lowStockCount} below reorder threshold` : 'All products above threshold'}</p>
                    </motion.div>

                    {/* Out of Stock */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-rose-200 shadow-sm p-4 cursor-pointer hover:shadow-md transition-all"
                        onClick={() => { setFilter('out'); setPage(1); }}>
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-rose-400 to-rose-600"><XCircle className="w-4 h-4 text-white" /></div>
                            {outOfStockCount > 0 && <span className="text-[10px] text-rose-600 font-semibold">{totalProductCount > 0 ? `${Math.round((outOfStockCount / totalProductCount) * 100)}%` : '0%'}</span>}
                        </div>
                        <p className={clsx('text-2xl font-bold', outOfStockCount > 0 ? 'text-rose-600' : 'text-slate-900')}>{outOfStockCount.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{outOfStockCount > 0 ? 'Urgent — restock immediately' : 'No products out of stock'}</p>
                    </motion.div>

                    {/* Confirmed */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-4">
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600"><ShieldCheck className="w-4 h-4 text-white" /></div>
                            <span className="text-[10px] text-emerald-600 font-semibold">{totalProductCount > 0 ? `${Math.round((confirmedCount / totalProductCount) * 100)}%` : '0%'}</span>
                        </div>
                        <p className="text-2xl font-bold text-emerald-700">{confirmedCount.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">Sellable · Confirmed items</p>
                    </motion.div>

                    {/* Pending */}
                    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        className="bg-white rounded-2xl border border-amber-200 shadow-sm p-4">
                        <div className="flex items-center justify-between mb-1">
                            <div className="p-2 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600"><Clock className="w-4 h-4 text-white" /></div>
                            {pendingCount > 0 && <span className="text-[10px] text-amber-600 font-semibold">{totalProductCount > 0 ? `${Math.round((pendingCount / totalProductCount) * 100)}%` : '0%'}</span>}
                        </div>
                        <p className={clsx('text-2xl font-bold', pendingCount > 0 ? 'text-amber-600' : 'text-slate-900')}>{pendingCount.toLocaleString()}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{pendingCount > 0 ? 'Waiting for physical count review' : 'All stock reviewed'}</p>
                    </motion.div>
                </div>

                {/* Low Stock Alert Banner */}
                <LowStockAlertBanner alerts={alerts} />

                {/* Filter Bar */}
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-1.5">
                    <div className="flex flex-col lg:flex-row gap-1.5">
                        {/* Search */}
                        <div className="relative flex-1">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                                type="text"
                                placeholder="Search by product name, SKU, or scan barcode..."
                                value={search}
                                onChange={(e) => {
                                    setSearch(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full pl-12 pr-12 py-3 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400 focus:bg-white transition-all text-sm"
                            />
                            {search && (
                                <button
                                    onClick={() => {
                                        setSearch('');
                                        setPage(1);
                                    }}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-lg text-slate-400 hover:text-slate-600"
                                    type="button"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            )}
                        </div>

                        {/* Branch Selector */}
                        {!isSingleStore && branchesList.length > 0 && (
                            <select
                                value={branchId}
                                onChange={(e) => {
                                    setBranchId(e.target.value);
                                    setPage(1);
                                }}
                                className="px-4 py-3 border-2 border-slate-200 rounded-xl text-sm bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-400"
                            >
                                <option value="">All Branches</option>
                                {branchesList.map((b: any) => (
                                    <option key={b.id} value={b.id}>
                                        {b.name}
                                    </option>
                                ))}
                            </select>
                        )}

                        {/* Stock Status Filter */}
                        <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                            {(
                                [
                                    'all',
                                    'in_stock',
                                    'low',
                                    'out',
                                ] as StockFilter[]
                            ).map((f) => (
                                <button
                                    key={f}
                                    onClick={() => {
                                        setFilter(f);
                                        setPage(1);
                                    }}
                                    className={clsx(
                                        'px-3 py-2 rounded-lg text-xs font-medium transition-all capitalize',
                                        filter === f
                                            ? 'bg-white text-emerald-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                    )}
                                    type="button"
                                >
                                    {f === 'in_stock'
                                        ? 'In Stock'
                                        : f}
                                </button>
                            ))}
                        </div>

                        {/* Confirmation Status Filter */}
                        <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                            {(
                                [
                                    'all',
                                    'pending',
                                    'counted',
                                    'confirmed',
                                ] as ConfirmationFilter[]
                            ).map((f) => (
                                <button
                                    key={f}
                                    onClick={() => {
                                        setConfirmationFilter(f);
                                        setPage(1);
                                    }}
                                    className={clsx(
                                        'px-3 py-2 rounded-lg text-xs font-medium transition-all capitalize',
                                        confirmationFilter === f
                                            ? 'bg-white text-emerald-600 shadow-sm'
                                            : 'text-slate-500 hover:text-slate-700'
                                    )}
                                    type="button"
                                >
                                    {f}
                                </button>
                            ))}
                        </div>

                        {/* Sort */}
                        <select
                            value={`${sortBy}-${order}`}
                            onChange={(e) => {
                                const [field, dir] = e.target.value.split(
                                    '-'
                                ) as [SortField, SortOrder];
                                setSortBy(field);
                                setOrder(dir);
                            }}
                            className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium text-slate-600"
                        >
                            <option value="updatedAt-desc">
                                Latest First
                            </option>
                            <option value="updatedAt-asc">
                                Oldest First
                            </option>
                            <option value="productName-asc">
                                Name A-Z
                            </option>
                            <option value="productName-desc">
                                Name Z-A
                            </option>
                            <option value="quantityOnHand-desc">
                                Highest Stock
                            </option>
                            <option value="quantityOnHand-asc">
                                Lowest Stock
                            </option>
                        </select>

                        {/* View Toggle */}
                        <div className="flex items-center bg-slate-100 rounded-xl p-0.5">
                            <button
                                onClick={() => setViewMode('table')}
                                className={clsx(
                                    'p-2 rounded-lg transition-all',
                                    viewMode === 'table'
                                        ? 'bg-white shadow-sm text-slate-700'
                                        : 'text-slate-400'
                                )}
                                type="button"
                            >
                                <List className="w-4 h-4" />
                            </button>
                            <button
                                onClick={() => setViewMode('card')}
                                className={clsx(
                                    'p-2 rounded-lg transition-all',
                                    viewMode === 'card'
                                        ? 'bg-white shadow-sm text-slate-700'
                                        : 'text-slate-400'
                                )}
                                type="button"
                            >
                                <LayoutGrid className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Clear Filters */}
                        {hasActiveFilters && (
                            <button
                                onClick={clearFilters}
                                className="flex items-center gap-1 px-3 py-2 text-xs font-medium text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 rounded-lg transition-colors whitespace-nowrap"
                                type="button"
                            >
                                <X className="w-3.5 h-3.5" /> Clear
                            </button>
                        )}
                    </div>
                </div>

                {/* Content */}
                {isLoading && !stockResponse ? (
                    <div className="flex justify-center py-20">
                        <Spinner size="xl" />
                    </div>
                ) : isError ? (
                    <ErrorState
                        title="Failed to load stock levels"
                        onRetry={() => refetch()}
                    />
                ) : items.length === 0 ? (
                    <EmptyState
                        icon={<Layers className="w-20 h-20" />}
                        title={
                            hasActiveFilters
                                ? 'No matching stock items'
                                : 'No stock data yet'
                        }
                        description={
                            hasActiveFilters
                                ? 'Try adjusting your search or filters.'
                                : 'Stock levels will appear after products are received or initial stock is set up.'
                        }
                        action={
                            hasActiveFilters
                                ? {
                                    label: 'Clear Filters',
                                    onClick: clearFilters,
                                }
                                : {
                                    label: 'Set Up Initial Stock',
                                    onClick: () =>
                                        navigate(
                                            '/inventory/setup'
                                        ),
                                }
                        }
                    />
                ) : (
                    <>
                        {/* Stock List */}
                        {viewMode === 'card' ? (
                            <StockCardView
                                items={items}
                                navigate={navigate}
                            />
                        ) : (
                            <StockTableView
                                items={items}
                                isSingleStore={isSingleStore}
                                navigate={navigate}
                            />
                        )}

                        {/* Pagination */}
                        {totalPages > 1 && (
                            <Pagination
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={paginationTotalCount}
                                pageSize={pageSize}
                                onPageChange={handlePageChange}
                                onPageSizeChange={(size) => {
                                    setPageSize(size);
                                    setPage(1);
                                }}
                            />
                        )}

                        {/* Show count when loading indicator */}
                        {isFetching && stockResponse && (
                            <div className="flex items-center justify-center gap-2 text-xs text-slate-400 py-2">
                                <Spinner size="xs" /> Updating...
                            </div>
                        )}
                    </>
                )}

                {/* Footer Navigation */}
                <div className="flex items-center justify-center gap-4 text-xs text-slate-400">
                    <button
                        onClick={() =>
                            navigate('/inventory/counts')
                        }
                        className="flex items-center gap-1 hover:text-emerald-600 transition-colors"
                        type="button"
                    >
                        <ClipboardList className="w-3.5 h-3.5" />{' '}
                        Stock Count
                    </button>
                    <span>·</span>
                    <button
                        onClick={() =>
                            navigate('/inventory/transfers')
                        }
                        className="flex items-center gap-1 hover:text-purple-600 transition-colors"
                        type="button"
                    >
                        <ArrowRightLeft className="w-3.5 h-3.5" />{' '}
                        Transfers
                    </button>
                    <span>·</span>
                    <button
                        onClick={() =>
                            navigate('/inventory/ledger')
                        }
                        className="flex items-center gap-1 hover:text-blue-600 transition-colors"
                        type="button"
                    >
                        <History className="w-3.5 h-3.5" /> Ledger
                    </button>
                    <span>·</span>
                    <button
                        onClick={() =>
                            navigate('/inventory/adjust')
                        }
                        className="flex items-center gap-1 hover:text-amber-600 transition-colors"
                        type="button"
                    >
                        <RotateCcw className="w-3.5 h-3.5" /> Adjust
                    </button>
                </div>
            </div>
        </div>
    );
}